"use client";
import * as React from "react";
import { StaticImageData } from "next/image";

import Button from "@stadium/shared/src/components/shared/button";
import Multimedia from "@stadium/shared/src/components/shared/multimedia";

import { B_02, BANNER_DEFAULTS } from "@stadium/shared/src/helpers/plasmic/static-slices";

import styles from "./main.module.scss";

export interface SlideItem {
  heading: string;
  description: string;
  image: string | StaticImageData | { url: string };
  imageAlt: string;
}

const getImageSrc = (image: SlideItem["image"]): string | StaticImageData =>
  typeof image === "object" && "url" in image ? image.url : image;

export interface B02Props {
  // Slides
  slides?: SlideItem[];
  sectionHeading?: string;

  // Layout & Styling
  backgroundColor?: string;
  className?: string;
  id?: string;
}

const B02: React.FC<Readonly<B02Props>> = ({
  slides = BANNER_DEFAULTS[B_02].slides,
  sectionHeading = "Finishes That Speak for Themselves.",
  backgroundColor = "#f5f5f5",
  className = "",
  id,
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [prevIndex, setPrevIndex] = React.useState(0);
  const [direction, setDirection] = React.useState<"next" | "prev">("next");
  const [isInitialRender, setIsInitialRender] = React.useState(true);

  const hasSlides = slides && slides.length > 0;
  const slidesLength = slides?.length || 0;

  const handleNext = React.useCallback(() => {
    setIsInitialRender(false);
    setDirection("next");
    setPrevIndex(currentIndex);
    setCurrentIndex((prev) => (prev + 1) % slidesLength);
  }, [currentIndex, slidesLength]);

  const handlePrev = React.useCallback(() => {
    setIsInitialRender(false);
    setDirection("prev");
    setPrevIndex(currentIndex);
    setCurrentIndex((prev) => (prev - 1 + slidesLength) % slidesLength);
  }, [currentIndex, slidesLength]);

  React.useEffect(() => {
    if (slidesLength <= 1) return;

    const timer = setInterval(() => {
      handleNext();
    }, 3500); // 3.5 seconds

    return () => clearInterval(timer);
  }, [handleNext, slidesLength]);

  const currentSlide = hasSlides ? slides[currentIndex] : null;
  const currentSlideImageUrl = currentSlide ? getImageSrc(currentSlide.image) : null;

  if (!hasSlides) {
    return (
      <section className={`${styles.sectionWrapper} ${className}`} id={id}>
        <div className={styles.divWrapper}>
          <div className={styles.contentWrapper}>
            <div className={styles.leftContentWrapper} style={{ backgroundColor }}>
              <div className={styles.leftContent}>
                <div className={styles.slideContent}>
                  <h2>No slides available</h2>
                  <p>Please add slides to display content.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  let carouselDirectionClass = "";
  if (!isInitialRender) {
    carouselDirectionClass = direction === "prev" ? styles.prev : styles.next;
  }

  return (
    <section className={`${styles.sectionWrapper} ${className}`} id={id}>
      <div className={styles.divWrapper}>
        <h2 className={styles.sectionHeading}>{sectionHeading}</h2>
        <div className={styles.contentWrapper}>
          <div className={styles.leftContentWrapper} style={{ backgroundColor }}>
            <div className={styles.leftContent}>
              <div key={currentIndex} className={styles.slideContent}>
                <h2 className={styles.heading}>{currentSlide.heading}</h2>
                {currentSlide.description && (
                  <p className={styles.description}>{currentSlide.description}</p>
                )}
              </div>
              {slidesLength > 1 && (
                <div className={styles.buttonContainer}>
                  <Button
                    styleType="no-style"
                    className={styles.buttonWrapperContainer}
                    onClick={handlePrev}
                    aria-label="Previous slide"
                  >
                    <span className={styles.prevBtn} />
                  </Button>
                  <Button
                    styleType="no-style"
                    className={styles.buttonWrapperContainer}
                    onClick={handleNext}
                    aria-label="Next slide"
                  >
                    <span className={styles.nextBtn} />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className={styles.rightContent}>
            <div className={`${styles.carousel} ${carouselDirectionClass}`}>
              {prevIndex !== currentIndex && (
                <div key={`${prevIndex}-${currentIndex}-leave`} className={styles.slideLeave}>
                  <Multimedia
                    type="image"
                    src={getImageSrc(slides[prevIndex].image)}
                    alt={slides[prevIndex].imageAlt}
                    className={styles.images}
                  />
                </div>
              )}

              <div key={`${currentIndex}-${direction}-enter`} className={styles.slideEnter}>
                <Multimedia
                  type="image"
                  src={currentSlideImageUrl}
                  alt={currentSlide.imageAlt}
                  className={styles.images}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default B02;
